import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../common/LoadingSpinner';
import Pagination from '../../common/Pagination';
import { getAdUrl } from '../../../utils/urlHelpers';
import { adService } from '../../../services/adService';
import SocietySectionToolbar from './SocietySectionToolbar';
import SectionFilterSelect from './SectionFilterSelect';
import { getAdCoverImageUrl, IMAGE_PLACEHOLDER_PATH } from '../../../utils/imageFallback';

const SocietyAds = ({ society, onError }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    sort: 'newest'
  });

  const loadAds = useCallback(async () => {
    if (!society?.id && !society?.slug) return;
    setLoading(true);
    try {
      const ordering = filters.sort === 'oldest' ? 'created_at' : filters.sort === 'price_low' ? 'price' : filters.sort === 'price_high' ? '-price' : '-created_at';
      const params = {
        page: currentPage,
        society: society.id ?? society.slug,
        ordering,
        search: filters.search || undefined,
        category: filters.category !== 'all' ? filters.category : undefined
      };
      const response = await adService.getAds(params);
      const results = response.results ?? response;
      const count = response.count ?? (Array.isArray(results) ? results.length : 0);
      setAds(Array.isArray(results) ? results : []);
      setTotalCount(count);
      const pageSize = 12;
      setTotalPages(Math.max(1, Math.ceil(count / pageSize)));
    } catch (error) {
      console.error('Error loading ads:', error);
      setAds([]);
      setTotalPages(1);
      setTotalCount(0);
      if (onError) onError('Failed to load ads');
    } finally {
      setLoading(false);
    }
  }, [society?.id, society?.slug, currentPage, filters.search, filters.category, filters.sort, onError]);

  useEffect(() => {
    loadAds();
  }, [loadAds]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const activeFilterCount =
    (filters.category !== 'all' ? 1 : 0) + (filters.sort !== 'newest' ? 1 : 0);

  return (
    <div className="space-y-4 sm:space-y-6">
      <SocietySectionToolbar
        title="Ads"
        count={totalCount}
        itemLabel={{ singular: 'ad', plural: 'ads' }}
        societyName={society.name}
        searchPlaceholder="Search ads..."
        searchValue={filters.search}
        onSearchChange={(v) => handleFilterChange({ ...filters, search: v })}
        activeFilterCount={activeFilterCount}
        addButton={
          isAuthenticated
            ? {
                label: 'Post Ad',
                onClick: () =>
                  navigate(`/societies/${society.slug}/ads/create?society=${society.id}`),
              }
            : null
        }
      >
        <SectionFilterSelect
          label="Category"
          value={filters.category}
          onChange={(e) => handleFilterChange({ ...filters, category: e.target.value })}
        >
          <option value="all">All Categories</option>
          <option value="automotive">Automotive</option>
          <option value="furniture">Furniture</option>
          <option value="electronics">Electronics</option>
          <option value="clothing">Clothing</option>
          <option value="books">Books</option>
          <option value="sports">Sports</option>
        </SectionFilterSelect>
        <SectionFilterSelect
          label="Sort By"
          value={filters.sort}
          onChange={(e) => handleFilterChange({ ...filters, sort: e.target.value })}
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="price_low">Price: Low to High</option>
          <option value="price_high">Price: High to Low</option>
        </SectionFilterSelect>
      </SocietySectionToolbar>

      {/* Ads Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3 sm:gap-6">
        {loading ? (
          <div className="col-span-full flex justify-center py-8">
            <LoadingSpinner size="medium" />
          </div>
        ) : ads.length > 0 ? (
          ads.map((ad) => (
            <div 
              key={ad.id} 
              className="bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer"
              onClick={() => navigate(getAdUrl(ad, society.slug))}
            >
              {/* Ad Image */}
              <div className="h-48 bg-gray-200 flex items-center justify-center">
                <img
                  src={getAdCoverImageUrl(ad)}
                  alt={ad.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = IMAGE_PLACEHOLDER_PATH;
                  }}
                />
              </div>

              {/* Ad Info */}
              <div className="p-3 sm:p-4">
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 line-clamp-3 leading-snug">
                  {ad.title}
                </h3>

                <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                  {ad.description}
                </p>

                <div className="text-xl font-bold text-blue-600 mb-4">
                  {formatPrice(ad.price)}
                </div>

                <div className="flex space-x-2">
                  <button className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors duration-200">
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full bg-white rounded-lg shadow-sm border p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No ads yet</h3>
            <p className="text-gray-500 mb-4">Be the first to post an ad in this society.</p>
            {isAuthenticated && (
              <button className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200">
                Post First Ad
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default SocietyAds;
